import { ref, type Ref } from 'vue';
import {
  listFunctionalActivities,
  listPositions,
  type RecordConfigOption,
} from '../api/record-config-options.api';

export interface UseRecordConfigSubjects {
  positions: Ref<RecordConfigOption[]>;
  functionalActivities: Ref<RecordConfigOption[]>;
  isLoading: Ref<boolean>;
  hasError: Ref<boolean>;
  load: () => Promise<void>;
}

/**
 * A failed list stays empty and only raises `hasError`: the selectors show their own error state,
 * and a toast here would stack on the one `useRecordConfig` raises when the form cannot load.
 */
export function useRecordConfigSubjects(): UseRecordConfigSubjects {
  const positions = ref<RecordConfigOption[]>([]);
  const functionalActivities = ref<RecordConfigOption[]>([]);
  const isLoading = ref(false);
  const hasError = ref(false);
  let loadPromise: Promise<void> | null = null;

  async function fetchAll(): Promise<void> {
    isLoading.value = true;
    try {
      const [positionResult, activityResult] = await Promise.allSettled([
        listPositions(),
        listFunctionalActivities(),
      ]);
      if (positionResult.status === 'fulfilled') positions.value = positionResult.value;
      if (activityResult.status === 'fulfilled') functionalActivities.value = activityResult.value;
      hasError.value = positionResult.status === 'rejected' || activityResult.status === 'rejected';
    } finally {
      isLoading.value = false;
    }
  }

  function load(): Promise<void> {
    loadPromise ??= fetchAll();

    return loadPromise;
  }

  return { positions, functionalActivities, isLoading, hasError, load };
}
